/**
 * Application configuration
 */

// Base API URLs
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL_RAW = process.env.REACT_APP_API_URL_RAW || 'http://localhost:5000';

// Configuration object
export const config = {
  // Base URLs
  BASE_URL: API_BASE_URL,
  BASE_URL_RAW: API_BASE_URL_RAW,

  // Authentication endpoints
  LOGIN_URL: `${API_BASE_URL}/login`,
  LOGGED_IN_USER: `${API_BASE_URL}/logged-in-user`,
  REGISTER_URL: `${API_BASE_URL}/register`,
  CREATE_USER_META: `${API_BASE_URL}/create-user-meta`,
  CREATE_USER_S3_FOLDERS: `${API_BASE_URL}/create-user-s3-folders`,

  // AWS Configuration (PUBLIC values only — region + endpoint + bucket display URL).
  // PR27 (P3-S5 incident remediation) removed the dead `AWS_ACCESS_KEY_ID` and
  // `AWS_SECRET_ACCESS_KEY` exports. They were a leftover from before the
  // server-mediated presigned-URL migration and were never consumed at runtime
  // (only one commented-out reference in `pages/subPages/ArticleEditor.jsx`).
  // ALL S3 access from the browser flows via:
  //   1. axios → server endpoints (signed by EC2 instance profile), or
  //   2. `refreshS3Credentials` thunk (`slices/coreAssetsSlice.jsx`) which
  //      fetches a short-lived presigned policy from the server for Froala.
  // Re-introducing long-lived AWS creds here is a deploy-blocking grep gate
  // (`AKIA[A-Z0-9]{16}` scan over `client/build` — see runbook Phase 9).
  AWS_REGION: process.env.REACT_APP_AWS_REGION || "us-west-1",
  AWS_END_POINT: "s3.us-west-1.amazonaws.com",
  AWS_AI_BUCKET: process.env.REACT_APP_AWS_AI_BUCKET || "https://aai-api.s3.us-west-1.amazonaws.com/",

  // Authorator Data
  AUTHORATOR_AI_BUCKET: process.env.REACT_APP_AUTHORATOR_AI_BUCKET || "aai-api",
  AUTHORATOR_USER_DATA: process.env.REACT_APP_AUTHORATOR_USER_DATA || 'aai-users/',
  AUTHORATOR_CORE_DATA: process.env.REACT_APP_AUTHORATOR_CORE_DATA || 'aai-core/',

  // API Utilities
  ENCRYPT_FIELD: `${API_BASE_URL}/encryptField`,
  // DECRYPT_FIELD removed in P2-cleanup; server-side route deleted.
  API_GET_SIGNED_URL_BY_TYPE: `${API_BASE_URL}/get-signed-url-by-type`,
  API_GET_SIGNED_URL_BY_PATH: `${API_BASE_URL}/get-signed-url-by-path`,
  API_GET_SIGNED_URL_BY_RAW_PATH: `${API_BASE_URL}/get-signed-url-by-raw-path`,
  API_PATH_DEFINITIONS: `${API_BASE_URL}/path-definitions`,
  API_TEST_PATH_RESOLUTION: `${API_BASE_URL}/test-path-resolution`,
  API_MEDIA_UPLOAD: `${API_BASE_URL}/upload-media`,

  // SSE Messaging
  API_SSE_ENDPOINT: `${API_BASE_URL}/sse-endpoint`,
  API_SSE_RESET_MSG_ENDPOINT: `${API_BASE_URL}/sse-reset-messages`,

  // Voice
  VOICE_CLONE_SNIPPET_FOLDER: process.env.REACT_APP_AUDIO_SNIPPET_FOLDER || 'voice-clone-snippets/',
  AUDIO_SNIPPET_FOLDER: process.env.REACT_APP_AUDIO_SNIPPET_FOLDER || 'voice-clone-snippets/',
  ADD_API_VOICE_META_DATA: `${API_BASE_URL}/add-voice-snippet`,
  GET_API_VOICE_CLONE_SNIPPETS: `${API_BASE_URL}/voice-clone-snippets`,
  GET_API_VOICE_SNIPPETS: `${API_BASE_URL}/voice-snippets`,
  GET_API_USER_ASSET_TREE: `${API_BASE_URL}/userAssetsData`,
  GET_API_USER_COUNTS: `${API_BASE_URL}/user-counts`,
  GET_API_LIT_GROUP_TREE: `${API_BASE_URL}/userLitGroupTree`,
  ADD_API_LIT_GROUP_AND_BOOKS: `${API_BASE_URL}/add-lit-group-and-books`,
  ADD_API_LIT_GROUP_BOOK: `${API_BASE_URL}/add-lit-group-book`,
  ADD_API_UPDATE_LIT_GROUP: `${API_BASE_URL}/add-update-lit-group`,
  DELETE_API_LIT_GROUP: `${API_BASE_URL}/delete-lit-group`,
  // The route that actually exists. DELETE_API_LIT_GROUP above points at /delete-lit-group,
  // which the server does not serve — its only caller is a thunk nothing dispatches.
  DELETE_API_LITERARY_GROUP: `${API_BASE_URL}/delete-literary-group`,

  // User Literary Group Types
  GET_API_USER_LIT_GRP_TYPES: `${API_BASE_URL}/user-std-constants`,
  UPLOAD_API_AUDIO_SNIPPET: `${API_BASE_URL}/upload-audio-snippet`,
  // DEPRECATED: Incorrectly named, use POST_API_ADD_CHAR_TO_BOOK instead
  GET_API_ADD_CHAR_TO_BOOK: `${API_BASE_URL}/add-char-to-book`,
  // New correctly named constant
  POST_API_ADD_CHAR_TO_BOOK: `${API_BASE_URL}/add-char-to-book`,

  // Statistics endpoints
  GET_API_STATISTICS: `${API_BASE_URL}/statistics`,
  GET_API_USER_STATISTICS: `${API_BASE_URL}/user/statistics`,
  GET_API_ROLE_STATISTICS: `${API_BASE_URL}/statistics/by-role`,

  // LitGroup Prototype endpoints
  API_LITGROUP_PROTOTYPES: `${API_BASE_URL}/litgroup-prototypes`,
  API_LITGROUP_PROTOTYPE_BOOKS: `${API_BASE_URL}/litgroup-prototypes`,  // + /:uuid/books
  API_LITGROUP_PROTOTYPE_AUTHOR_IMAGE: `${API_BASE_URL}/litgroup-prototypes`, // + /:uuid/author-image
  API_LITGROUP_PROTOTYPE_STATUS: `${API_BASE_URL}/litgroup-prototypes`, // + /:uuid/status
  API_LITGROUPS: `${API_BASE_URL}/litgroups`, // + /:litGroupUuid/books  (add a book to an already-published LitGroup)

  // Text Sample
  TEXT_SAMPLE_SNIPPET_FOLDER: process.env.REACT_APP_TEXT_SAMPLE_SNIPPET_FOLDER || 'text-sample-snippets/',
  GET_API_TEXT_SNIPPETS: `${API_BASE_URL}/text-snippets`,
  GET_API_TEXT_CLONE_SNIPPETS: `${API_BASE_URL}/text-clone-snippets`,
  ADD_API_TEXT_SAMPLE_META_DATA: `${API_BASE_URL}/add-text-snippet`,
  ADD_API_TEXT_SNIPPET_OVERRIDE: `${API_BASE_URL}/add-text-snippet-override`,
  GET_API_TEXT_SNIPPET_OVERRIDE: `${API_BASE_URL}/get-text-snippet-override`,

  UPLOAD_API_TEXT_SNIPPET: `${API_BASE_URL}/upload-text-snippet`,

  // Snippet Pairs (text and associated voice)
  GET_API_TEXT_VOICE_SNIPPET_PAIR: `${API_BASE_URL}/text-voice-snippet-pair`,
  GET_API_TEXT_VOICE_SNIPPET_PAIRS: `${API_BASE_URL}/text-voice-snippet-pairs`,

  GET_API_ALL_CHAR_SPEECH_SAMPLES: `${API_BASE_URL}/char-speech-samples`,

  GET_API_ALL_SAMPLES: `${API_BASE_URL}/text-voice-samples`,
  GET_API_SAMPLE: `${API_BASE_URL}/text-voice-sample`,
  DELETE_API_SAMPLE: `${API_BASE_URL}/delete-character_speech_sample/:targetUuid`,
  DELETE_API_SAMPLE_DETAILS: `${API_BASE_URL}/delete-voice-sample-details`,

  // Article Api Urls
  ADD_API_ARTICLE: `${API_BASE_URL}/add-new-articleitem`,
  DELETE_API_ARTICLE: `${API_BASE_URL}/delete-articleitem`,

  GET_API_ARTICLE: `${API_BASE_URL}/get-articleitem`,
  GET_API_ARTICLES: `${API_BASE_URL}/get-articles`,

  // The email-template registry, for the Article Editor's subtype dropdown. Admin-gated.
  // ⭐ An endpoint rather than a copy in the client: a duplicated registry drifts, and a subtype
  // the editor offers but the renderer does not declare is a template that never matches a lookup.
  API_EMAIL_SUBTYPES: `${API_BASE_URL}/email-template-subtypes`,

  GET_FULL_BOOK_BY_USER: `${API_BASE_URL}/get-book-full-data`,
  GET_ALL_BOOKS_BY_USER: `${API_BASE_URL}/get-books-by-user`,
  ADD_API_STORY_BOOK_META_DATA: `${API_BASE_URL}/add-book-by-user`,
  UPLOAD_API_BOOK_SOURCE: `${API_BASE_URL}/upload-book-source`,
  PROCESS_API_PDF_BOOK_SOURCE: `${API_BASE_URL}/process-pdf-book-source`,

  GET_BOOK_DATA: `${API_BASE_URL}/get-book-tree`,
  GET_BOOK_JSON: `${API_BASE_URL}/get-book-json/:userId/:bookUuid`,
  CREATE_BOOK_PROCESS_NODES: `${API_BASE_URL}/create-book-process-nodes/`,
  COPY_BOOK_ASSETS: `${API_BASE_URL}/copy-book-assets`,
  GET_SPEECH_SAMPLE_DATA: `${API_BASE_URL}/get-speech-sample-data`,
  GET_VARIATION_LIST: `${API_BASE_URL}/get-common-variation-list`,
  GET_HIGHEST_REVISION: `${API_BASE_URL}/get-highest_revision`,
  GET_FORMATED_TEXT: '/api/get-formatted-text',

  ADD_VARIATION_TO_COMMON_LIST: `${API_BASE_URL}/add-variation-to-common-list`,
  ADD_VARIATION_TO_BOOK_CHARACTER: `${API_BASE_URL}/add-variation-to-book-character`,
  ADD_CHARACTER_SPEECH_SAMPLE: `${API_BASE_URL}/add-character_speech_sample`,
  ADD_CHARACTER_SPEECH_SAMPLE_V2: `${API_BASE_URL}/add-character-speech-sample-v2`,
  ADD_VA_CHARACTER_SPEECH_SAMPLE_V2: `${API_BASE_URL}/va-add-character-speech-sample-v2`,
  ADD_VA_CHARACTER_SPEECH_SAMPLE_V3: `${API_BASE_URL}/va-add-character-speech-sample-v3`,

  ADD_EMPTY_CHARACTER_SPEECH_SAMPLE: `${API_BASE_URL}/add-empty-character_speech_sample`,

  REMOVE_VARIATION_FROM_BOOK_CHARACTER: `${API_BASE_URL}/remove-character-variation`,

  AAI_API_MOVE_FILE: `${API_BASE_URL}/move-file/aai-api`,
  API_UPLOAD_FILE: `${API_BASE_URL}/upload_file`,
  SNIPSEQ_DELETE_FILE: `snip_seq_delete_snippet`,
  SNIPSEQ_DELETE_FILES: `snip_seq_delete_snippets`,
  IMAGE_UPLOAD_URL: `${API_BASE_URL}/upload-temp-image/temp`,
  UPLOAD_API_PROFILE_IMAGE: `${API_BASE_URL}/upload-profile-image/:userId`,
  // Froala Pro license key. Updated 2026-07-13 (previous key expired -> "Expired
  // copy of Froala Editor" on the production domain; localhost is license-exempt
  // so dev never surfaced it). Old key kept below (commented) for history.
  // FORMIX_KEY: '2J1B10dA5F5C4F4C3B3I3C-22VKOG1FGULVKHXDXNDXc2a1Kd1SNdF3H3A8D7D4F4A3E3D2A15==',
  FORMIX_KEY: 'WE1B5dC3F3G3A7A7D7A4E4QgqtyQNDMIJg1IQNSEa1EUAi1XVFQd1EaG3C2A5B3C4E3E2D4E2I2==',


  // API_SSE_NOTIFICATION: 'http://localhost:5000/api/sse-notification',
  // API_SSE_ENDPOINT: 'http://localhost:5000/api/sse-endpoint',
  // API_SSE_RESET_MSG_ENDPOINT: 'http://localhost:5000/api/sse-reset-messages',

  GET_CHAR_VAR_SAMPLES: `${API_BASE_URL}/get-char-var-samples`,
  GET_BOOK_CHAR_VAR_SAMPLES: `${API_BASE_URL}/get-book-char-var-samples`,
  GET_VARIATION_COUNTS: `${API_BASE_URL}/get-book-char-variation-counts`,

  // Direct fetch endpoints
  GET_API_DIRECT_LIT_GROUPS: `${API_BASE_URL}/direct/lit-groups`,
  GET_API_DIRECT_BOOKS_BY_LIT_GROUP: `${API_BASE_URL}/direct/lit-groups/:litGroupUuid/books`,
  PUT_API_DIRECT_LIT_GROUP_PROFILE: `${API_BASE_URL}/direct/lit-groups/:litGroupUuid/profile`,
  POST_API_DIRECT_LIT_GROUP: `${API_BASE_URL}/direct/lit-groups`,
  GET_API_BOOKS_ALL_FOR_USER: `${API_BASE_URL}/books/all-for-user`,

  GET_API_DIRECT_CHARACTERS_BY_BOOK: `${API_BASE_URL}/direct/books/:bookUuid/characters`,
  GET_API_DIRECT_CHARACTERS_ENHANCED: `${API_BASE_URL}/direct/books/:bookUuid/characters-enhanced`,
  GET_API_DIRECT_VARIATIONS_BY_CHARACTER: `${API_BASE_URL}/direct/characters/:characterUuid/variations`,
  GET_API_DIRECT_LIT_GROUP_PROFILE: `${API_BASE_URL}/direct/lit-groups/:litGroupUuid/profile`,
  GET_API_DIRECT_CHARACTER_BY_BOOK: `${API_BASE_URL}/direct/books/:bookUuid/characters/:characterUuid`,
  PUT_API_DIRECT_CHARACTER_BY_BOOK: `${API_BASE_URL}/direct/books/:bookUuid/characters/:characterUuid`,
  DELETE_API_BW_CHARACTER_UNIFIED: `${API_BASE_URL}/delete/bw-character`,

  // New direct fetch endpoints for variations

  POST_API_DIRECT_VARIATION: `${API_BASE_URL}/direct/characters/:characterUuid/variations`,
  PUT_API_DIRECT_VARIATION: `${API_BASE_URL}/direct/variations/:id`,
  PUT_API_VA_DIRECT_VARIATION: `${API_BASE_URL}/va-variations/:variationId`,
  DELETE_API_DIRECT_VARIATION: `${API_BASE_URL}/direct/variations/:id`,
  DELETE_API_VA_DIRECT_VARIATION: `${API_BASE_URL}/variations/:variationId`,

  GET_API_DIRECT_VARIATION: `${API_BASE_URL}/direct/variations/:id`,

  // direct update of an existing TextSnippet
  UPDATE_TEXT_SNIPPET: `${API_BASE_URL}/update-text-snippet`,

  // Book Profile endpoints
  GET_API_BOOK_PROFILE: `${API_BASE_URL}/books/:bookUuid/profile`,
  SAVE_API_BOOK_PROFILE: `${API_BASE_URL}/books/:bookUuid/profile`,
  PUT_API_BOOK_PROFILE: `${API_BASE_URL}/books/:bookUuid/profile`,
  DELETE_API_BOOK_PROFILE: `${API_BASE_URL}/books/:bookUuid/profile`,
  GET_API_COMPLETE_BOOK: `${API_BASE_URL}/books/:bookUuid/complete`,

  // =========================================================
  // STANDARDIZED API ENDPOINTS
  // =========================================================

  // Voice Actor Users
  VOICE_ACTORS_BASE: `${API_BASE_URL}/voice-actors`,
  VOICE_ACTORS_URL: `${API_BASE_URL}/voice-actors`,
  UPLOAD_VOICE_ACTOR_IMAGE: `${API_BASE_URL}/voice-actors/:id/image`,

  GET_API_DIRECT_VOICE_ACTOR_PROFILE: `${API_BASE_URL}/voice-actors/:actorId`,

  // const response = await apiClient.get(config.GET_API_DIRECT_VOICE_ACTOR_PROFILE || '/api/voice-actors/:actorId', { actorId });

  // Voice Actor Portfolios
  PORTFOLIOS_BASE: `${API_BASE_URL}/portfolios`,
  VOICE_ACTOR_PORTFOLIO_URL: `${API_BASE_URL}/portfolios`,
  GET_API_DIRECT_PORTFOLIOS_BY_ACTOR: `${API_BASE_URL}/voice-actors/:actorId/portfolios`,
  POST_API_CREATE_PORTFOLIO: `${API_BASE_URL}/voice-actors/:actorId/portfolios`,
  GET_API_DIRECT_PORTFOLIO_PROFILE: `${API_BASE_URL}/portfolios/:portfolioId`,
  PUT_API_UPDATE_PORTFOLIO: `${API_BASE_URL}/portfolios/:portfolioId`,
  GET_API_DIRECT_CHARACTERS_BY_PORTFOLIO: `${API_BASE_URL}/portfolios/:portfolioId/characters`,
  GET_API_DIRECT_CHARACTER_BY_PORTFOLIO: `${API_BASE_URL}/portfolios/:portfolioId/characters/:characterId`,
  POST_API_ADD_CHAR_TO_PORTFOLIO: `${API_BASE_URL}/portfolios/:portfolioId/characters`,
  PUT_API_DIRECT_CHARACTER_BY_PORTFOLIO: `${API_BASE_URL}/portfolios/:portfolioId/characters/:characterId`,
  DELETE_API_CHARACTER_BY_PORTFOLIO: `${API_BASE_URL}/portfolios/:portfolioId/characters/:characterId`,
  DELETE_API_VA_CHARACTER_UNIFIED: `${API_BASE_URL}/delete/va-character`,
  DELETE_API_VA_VARIATION_UNIFIED: `${API_BASE_URL}/delete/va-variation`,
  DELETE_API_VA_SPEECH_SAMPLE_UNIFIED: `${API_BASE_URL}/delete/va-speech-sample`,
  // Character endpoints        `/api/delete/va-character/${characterId}`,

  CHARACTERS_BASE: `${API_BASE_URL}/characters`,
  RECORDED_CHARACTERS_URL: `${API_BASE_URL}/characters`,
  UPLOAD_CHARACTER_AVATAR: `${API_BASE_URL}/characters/:id/avatar`,
  UPLOAD_CHARACTER_AVATAR_PRE: `${API_BASE_URL}/characters/avatar/pre`,
  DELETE_CHARACTER_AVATAR: `${API_BASE_URL}/characters/:id/avatar`,
  UPLOAD_DEFAULT_AVATAR: `${API_BASE_URL}/upload`,

  // Voice Lab Composite Data
  VOICE_LAB_COMPOSITE_DATA: `${API_BASE_URL}/voice-lab/composite-data`,

  // Variations
  VARIATIONS_BASE: `${API_BASE_URL}/variations`,
  CHARACTER_VARIATIONS_BASE: `${API_BASE_URL}/characters/:characterId/variations`,
  GET_ALL_CHARACTER_VARIATIONS: `${API_BASE_URL}/characters/:characterId/variations`,
  GET_CHARACTER_VARIATION: `${API_BASE_URL}/variations/:id`,
  CREATE_CHARACTER_VARIATION: `${API_BASE_URL}/characters/:characterId/variations`,
  UPDATE_CHARACTER_VARIATION: `${API_BASE_URL}/variations/:id`,
  DELETE_CHARACTER_VARIATION: `${API_BASE_URL}/variations/:id`,
  UPLOAD_VOICE_ACTOR_VARIATION_AUDIO: `${API_BASE_URL}/variations/:id/audio`,
  UPLOAD_BOOK_VARIATION_AUDIO: `${API_BASE_URL}/books/variations/:id/audio`,
  DELETE_API_VA_SAMPLE: `${API_BASE_URL}/samples/:versionId`,
  POST_API_VA_DIRECT_VARIATION: `${API_BASE_URL}/characters/:characterId/variations`,
  GET_VA_VARIATION_COUNTS: `${API_BASE_URL}/characters/:characterId/variation-counts`,
  GET_VA_CHAR_VAR_SAMPLES: `${API_BASE_URL}/va-variations/:variationId/samples`,
  // GET_VA_CHARACTER_VARIATION: `${API_BASE_URL}/variations/:characterId`,

  // Voice Actor Direct Fetch endpoints
  GET_VA_VARIATION_SAMPLES: `${API_BASE_URL}/va-variations/:variationId/samples`,
  GET_VA_CHARACTER_BY_PORTFOLIO: `${API_BASE_URL}/va-portfolios/:portfolioId/characters/:characterId`,
  GET_VA_CHARACTER_WITH_VARIATIONS: `${API_BASE_URL}/va-characters/:characterId/with-variations`,
  GET_VA_CHARACTER_VARIATIONS: `${API_BASE_URL}/va-portfolios/:portfolioId/characters/:characterId/with-variations`,
  DELETE_VA_SAMPLE: `${API_BASE_URL}/va-samples/:versionId`,
  GET_VA_PORTFOLIO_PROFILE: `${API_BASE_URL}/va-portfolios/:portfolioId/profile`,

  // Assignments
  ASSIGNMENTS_BASE: `${API_BASE_URL}/assignments`,
  VOICE_ACTOR_ASSIGNMENTS_URL: `${API_BASE_URL}/voice-actors/:actorId/assignments`,

  // Opportunities
  OPPORTUNITIES_BASE: `${API_BASE_URL}/opportunities`,
  CREATE_OPPORTUNITY: `${API_BASE_URL}/opportunities`,
  OPPORTUNITY_ZONE_GENRES: `${API_BASE_URL}/opportunities/zone/filters/genres`,
  GET_BOOK_OPPORTUNITIES: `${API_BASE_URL}/books/:bookId/opportunities`,
  GET_CHARACTER_OPPORTUNITIES: `${API_BASE_URL}/characters/:characterId/opportunities`,
  GET_RECOMMENDED_OPPORTUNITIES: `${API_BASE_URL}/voice-actors/:actorId/recommended-opportunities`,

  // Applications
  APPLICATIONS_BASE: `${API_BASE_URL}/applications`,
  CREATE_APPLICATION: `${API_BASE_URL}/applications`,
  GET_OPPORTUNITY_APPLICATIONS: `${API_BASE_URL}/opportunities/:opportunityId/applications`,
  GET_ACTOR_APPLICATIONS: `${API_BASE_URL}/voice-actors/:actorId/applications`,
  GET_RECORDING_TASKS: `${API_BASE_URL}/applications/:applicationId/recording-tasks`,
  UPDATE_SPEECH_SAMPLE_VOICE_URL: `${API_BASE_URL}/applications/speech-samples/:sampleId/voice-url`,
  SUBMIT_SPEECH_SAMPLE: `${API_BASE_URL}/applications/speech-samples/:sampleId/submit`,
  // Phase 4D — VA Fidelity Gate (P2 client). Stateless server-side scoring of
  // a voice-snippet recording against its expected prompt. Multipart upload;
  // returns the FidelityEnvelope shape consumed by the FidelityPanel.
  // Backed by server/routes/voice-fidelity-routes.js (POST_SCORE_VOICE_SNIPPET).
  SCORE_VOICE_SNIPPET: `${API_BASE_URL}/voice-snippets/score`,

  APPROVE_SPEECH_SAMPLE: `${API_BASE_URL}/applications/speech-samples/:sampleId/approve`,
  REJECT_SPEECH_SAMPLE: `${API_BASE_URL}/applications/speech-samples/:sampleId/reject`,

  // Voice-cloning pipeline (Book Creator)
  GET_VOICE_CLONE_PIPELINE: `${API_BASE_URL}/users/:userId/voice-clone-pipeline`,
  SEED_VOICE_CLONE: `${API_BASE_URL}/voice-clones/seed`,
  GET_VOICE_CLONE: `${API_BASE_URL}/voice-clones/:cloneUuid`,
  RETRY_VOICE_CLONE: `${API_BASE_URL}/voice-clones/:cloneUuid/retry`,
  RECONCILE_VOICE_CLONE: `${API_BASE_URL}/voice-clones/:cloneUuid/reconcile`,
  SMOKE_TEST_VOICE_CLONE: `${API_BASE_URL}/voice-clones/:cloneUuid/smoke-test`,
  DELETE_VOICE_CLONE: `${API_BASE_URL}/voice-clones/:cloneUuid`,
  // Phase-3 add: user-facing retry for the SampleNormalizer worker. The clone
  // gate in VoiceCloningService.seedCloneForSample refuses to ship an un-
  // normalized VoiceSnippet to ElevenLabs, so this is the escape hatch when
  // a row is stuck at normalizeStatus='failed' or the legacy 'none' (null).
  RETRY_VOICE_SNIPPET_NORMALIZE: `${API_BASE_URL}/voice-snippets/:vsUuid/normalize/retry`,

  // Voice-readiness stats (Phase 4a — BOOK_VOICE_STATS_SPEC §2)
  GET_BOOK_VOICE_STATS: `${API_BASE_URL}/books/:bookId/voice-stats`,
  GET_BOOK_SCRIPT_COVERAGE: `${API_BASE_URL}/books/:bookId/script-coverage`,
  GET_LITGROUP_VOICE_STATS: `${API_BASE_URL}/litgroups/:litGroupId/voice-stats`,

  // Voice rendering — dry-resolve surface (Phase 4c — Blocker 2 / D3)
  POST_VOICE_RENDERING_DRY_RESOLVE: `${API_BASE_URL}/voice-rendering/dry-resolve`,
  GET_VOICE_RENDERING_SNAPSHOT: `${API_BASE_URL}/voice-rendering/:bookId/snapshot`,

  // Voice rendering — Rendering Studio (Phase 4d Step 3). Five endpoints
  // backing the per-character "eye-test the 9-cell grid" page. Server side
  // lives in voiceRenderingController + GridRenderer.js; client thunks are
  // in voiceRenderingSlice.js.
  GET_VOICE_RENDERING_CHARACTERS: `${API_BASE_URL}/voice-rendering/:bookId/characters`,
  GET_VOICE_RENDERING_GRID: `${API_BASE_URL}/voice-rendering/:bookId/grid/:characterUuid`,
  POST_VOICE_RENDERING_RENDER_GRID: `${API_BASE_URL}/voice-rendering/:bookId/render-grid`,
  GET_VOICE_RENDERING_GRID_PROGRESS: `${API_BASE_URL}/voice-rendering/grid-progress/:jobId`,
  POST_VOICE_RENDERING_CELL_RESIGN: `${API_BASE_URL}/voice-rendering/cell/:cellId/resign`,
  // Phase 4d R3a — selector-driven render-unit pipeline
  GET_VOICE_RENDERING_RENDER_TREE: `${API_BASE_URL}/voice-rendering/:bookId/render-tree`,
  GET_VOICE_RENDERING_PREFLIGHT: `${API_BASE_URL}/voice-rendering/:bookId/preflight`,
  POST_VOICE_RENDERING_RENDER_UNIT: `${API_BASE_URL}/voice-rendering/:bookId/render-unit`,
  GET_VOICE_RENDERING_UNIT_PROGRESS: `${API_BASE_URL}/voice-rendering/unit-progress/:jobId`,
  GET_VOICE_RENDERING_RENDERED_LINES: `${API_BASE_URL}/voice-rendering/:bookId/rendered-lines`,
  GET_VOICE_RENDERING_RENDERED_LINE_URL: `${API_BASE_URL}/voice-rendering/rendered-line/:lineUuid/url`,
  GET_VOICE_RENDERING_STITCH_READINESS: `${API_BASE_URL}/voice-rendering/:bookId/stitch-readiness`,
  POST_VOICE_RENDERING_STITCH: `${API_BASE_URL}/voice-rendering/:bookId/stitch`,
  POST_VOICE_RENDERING_EXPORT: `${API_BASE_URL}/voice-rendering/:bookId/export`,
  GET_VOICE_RENDERING_AUDIO_LAB: `${API_BASE_URL}/voice-rendering/:bookId/audio-lab`,
  // Delivery — the finished book. ⭐ The artifact routes key on the ARTIFACT uuid, not the book: a
  // share link outlives the panel that made it and has to be re-mintable from the record alone.
  GET_VOICE_RENDERING_DELIVERY: `${API_BASE_URL}/voice-rendering/:bookId/delivery`,
  POST_VOICE_RENDERING_DELIVERY_ARTIFACT_LINK: `${API_BASE_URL}/voice-rendering/delivery-artifact/:uuid/link`,
  POST_VOICE_RENDERING_DELIVERY_ARTIFACT_VERIFY: `${API_BASE_URL}/voice-rendering/delivery-artifact/:uuid/verify`,
  POST_VOICE_RENDERING_SEAM_REEL: `${API_BASE_URL}/voice-rendering/:bookId/seam-reel`,
  POST_VOICE_RENDERING_MASTER: `${API_BASE_URL}/voice-rendering/:bookId/master`,
  GET_VOICE_RENDERING_MASTER_PROGRESS: `${API_BASE_URL}/voice-rendering/master-progress/:jobId`,
  POST_VOICE_RENDERING_MASTER_ABORT: `${API_BASE_URL}/voice-rendering/master/:jobId/abort`,
  GET_VOICE_RENDERING_STITCH_PROGRESS: `${API_BASE_URL}/voice-rendering/stitch-progress/:jobId`,
  // Ask a running stitch to stop after its current chapter.
  POST_VOICE_RENDERING_UNIT_RENDER_STOP: `${API_BASE_URL}/voice-rendering/unit-render-stop/:jobId`,
  POST_VOICE_RENDERING_STITCH_STOP: `${API_BASE_URL}/voice-rendering/stitch-stop/:jobId`,
  GET_VOICE_RENDERING_RENDERED_CHAPTER_URL: `${API_BASE_URL}/voice-rendering/rendered-chapter/:chapterUuid/url`,

  // Emotion→category translation matrix (admin-editable registry)
  API_EMOTION_CATEGORY_MATRIX: `${API_BASE_URL}/emotion-category/matrix`,
  API_EMOTION_CATEGORY_CACHE_REFRESH: `${API_BASE_URL}/emotion-category/cache/refresh`,
  API_EMOTION_CATEGORY_CATEGORIES: `${API_BASE_URL}/emotion-category/categories`,
  API_EMOTION_CATEGORY_GEN_TEMPLATE: `${API_BASE_URL}/emotion-category/gen-template`,

  // Character context — Phase 4D Hover Panel (Chunk 5 backend, Chunk 6 client).
  // VA-facing read-only endpoint returning a single envelope sized for the
  // hover popover. The envelope is rendered by `HoverPanelMockup` after the
  // adapter in `src/utils/characterContext/envelopeToPanelShape.js` translates
  // it into the mockup's prop shape. The path params are :bookUuid and
  // :characterUuid; useCharacterContext threads both through and the route
  // is auth-walled by the same `authMiddleware` as voice-rendering.
  GET_CHARACTER_CONTEXT: `${API_BASE_URL}/character-context/:bookUuid/:characterUuid`,


  // Default values
  DEFAULT_AVATAR: '/justBookStack.png',
  DEFAULT_BOOK_COVER: '/book_cover_icon.png',

  // Pagination
  ITEMS_PER_PAGE: 10,

  // Timeouts
  //
  // ⭐ THE DEFAULT IS WRONG FOR ANYTHING THAT CASCADES, AND SILENTLY SO. A 30-second ceiling on a
  // delete that takes minutes does not stop the work — the server finishes it — it just tells the
  // operator the delete FAILED when it succeeded. That has now happened three times: Chapel 13
  // (22,389 nodes, 195 seconds, reported as an error), the Admin user delete, and LG Canary 3.
  //
  // Named rather than a literal because the literal kept being retyped. Before this there were
  // three separate `timeout: 900000` in three files and one delete thunk that had none at all,
  // which is exactly the site that failed. See CLIENT_API_CLIENT_MIGRATION_PLAN.md §1.4.
  //
  // ⚠️ `apiClient.delete(url, params, config)` — the config is the THIRD argument, not the fourth
  // as it is on get/post/put/upload, because a DELETE carries no body. A timeout passed in the
  // fourth slot lands in nothing and the call keeps the 30-second default, silently. §1.5.
  REQUEST_TIMEOUT: 30000, // 30 seconds — fine for reads and small writes
  TIMEOUTS: {
    DEFAULT: 30000,
    LONG: 180000,      // 3 min  — ingest preview, prototype publish: slow, bounded work
    CASCADE: 900000,   // 15 min — deletes that walk a whole group/user and clean up S3
  },

  // Local storage keys
  STORAGE_TOKEN_KEY: 'authToken',
  STORAGE_USER_KEY: 'user',

  // Feature flags
  ENABLE_ANALYTICS: false,
  ENABLE_NOTIFICATIONS: true,
};

// Export all routes as a copy of config
export const allRoutes = {
  ...config,
  // Ensure authentication endpoints are explicitly set in allRoutes
  LOGIN_URL: config.LOGIN_URL,
  LOGGED_IN_USER: config.LOGGED_IN_USER,
  REGISTER_URL: config.REGISTER_URL
};

// US States for form dropdowns
export const allStates = [
  { key: 'AL', text: 'Alabama', value: 'AL' },
  { key: 'AK', text: 'Alaska', value: 'AK' },
  { key: 'AZ', text: 'Arizona', value: 'AZ' },
  { key: 'AR', text: 'Arkansas', value: 'AR' },
  { key: 'CA', text: 'California', value: 'CA' },
  { key: 'CO', text: 'Colorado', value: 'CO' },
  { key: 'CT', text: 'Connecticut', value: 'CT' },
  { key: 'DE', text: 'Delaware', value: 'DE' },
  { key: 'DC', text: 'District Of Columbia', value: 'DC' },
  { key: 'FL', text: 'Florida', value: 'FL' },
  { key: 'GA', text: 'Georgia', value: 'GA' },
  { key: 'HI', text: 'Hawaii', value: 'HI' },
  { key: 'ID', text: 'Idaho', value: 'ID' },
  { key: 'IL', text: 'Illinois', value: 'IL' },
  { key: 'IN', text: 'Indiana', value: 'IN' },
  { key: 'IA', text: 'Iowa', value: 'IA' },
  { key: 'KS', text: 'Kansas', value: 'KS' },
  { key: 'KY', text: 'Kentucky', value: 'KY' },
  { key: 'LA', text: 'Louisiana', value: 'LA' },
  { key: 'ME', text: 'Maine', value: 'ME' },
  { key: 'MD', text: 'Maryland', value: 'MD' },
  { key: 'MA', text: 'Massachusetts', value: 'MA' },
  { key: 'MI', text: 'Michigan', value: 'MI' },
  { key: 'MN', text: 'Minnesota', value: 'MN' },
  { key: 'MS', text: 'Mississippi', value: 'MS' },
  { key: 'MO', text: 'Missouri', value: 'MO' },
  { key: 'MT', text: 'Montana', value: 'MT' },
  { key: 'NE', text: 'Nebraska', value: 'NE' },
  { key: 'NV', text: 'Nevada', value: 'NV' },
  { key: 'NH', text: 'New Hampshire', value: 'NH' },
  { key: 'NJ', text: 'New Jersey', value: 'NJ' },
  { key: 'NM', text: 'New Mexico', value: 'NM' },
  { key: 'NY', text: 'New York', value: 'NY' },
  { key: 'NC', text: 'North Carolina', value: 'NC' },
  { key: 'ND', text: 'North Dakota', value: 'ND' },
  { key: 'OH', text: 'Ohio', value: 'OH' },
  { key: 'OK', text: 'Oklahoma', value: 'OK' },
  { key: 'OR', text: 'Oregon', value: 'OR' },
  { key: 'PA', text: 'Pennsylvania', value: 'PA' },
  { key: 'RI', text: 'Rhode Island', value: 'RI' },
  { key: 'SC', text: 'South Carolina', value: 'SC' },
  { key: 'SD', text: 'South Dakota', value: 'SD' },
  { key: 'TN', text: 'Tennessee', value: 'TN' },
  { key: 'TX', text: 'Texas', value: 'TX' },
  { key: 'UT', text: 'Utah', value: 'UT' },
  { key: 'VT', text: 'Vermont', value: 'VT' },
  { key: 'VA', text: 'Virginia', value: 'VA' },
  { key: 'WA', text: 'Washington', value: 'WA' },
  { key: 'WV', text: 'West Virginia', value: 'WV' },
  { key: 'WI', text: 'Wisconsin', value: 'WI' },
  { key: 'WY', text: 'Wyoming', value: 'WY' }
];

// Document filter options for playback selector
export const docFilterOptions = [
  { key: "dfo_1", text: 'No Filter', value: 'All' },
  { key: "dfo_2", text: 'Selected Snippets', value: 'Selected' },
  { key: "dfo_3", text: 'Sample Name', value: 'Sample Name' },
  { key: "dfo_4", text: 'Sample Text', value: 'Sample Text' },
  { key: "dfo_5", text: 'Sample Text Variation', value: 'Sample Text Variation' },
  { key: "dfo_6", text: 'Title', value: 'Title' },
];

// Items per page options for pagination
export const itemsPerPageOptions = [
  { key: 0, text: 'All Pages', value: '-1' },
  { key: 11, text: '1', value: '1' },
  { key: 12, text: '2', value: '2' },
  { key: 13, text: '3', value: '3' },
  { key: 1, text: '5', value: '5' },
  { key: 2, text: '10', value: '10' },
  { key: 3, text: '20', value: '20' },
  { key: 4, text: '50', value: '50' },
  { key: 5, text: '100', value: '100' }
];

// Literary Group Types for dropdowns
export const literaryGroupTypes = [
  { label: 'Anthology', value: 'anthology' },
  { label: 'Collection', value: 'collection' },
  { label: 'Cycle', value: 'cycle' },
  { label: 'Series', value: 'series' },
  { label: 'Standalone', value: 'standalone' },
];

// Froala Editor configuration for Voice Lab
export const formixConfig_VoiceLab = {
  iframe: false,
  key: config.FORMIX_KEY,
  attribution: false,
  htmlAllowComments: true,
  imageUploadParams: {},
  imageUploadParam: 'file',
  imageUploadURL: config.IMAGE_UPLOAD_URL,
  imageUploadMethod: 'POST',
  imageMaxSize: 5 * 1024 * 1024,
  fileMaxSize: 1024 * 1024 * 3,
  fileUploadMethod: 'PUT',
  fileUploadParam: 'file_name',
  fileUploadURL: '/api/upload_file',
  fileUploadParams: { id: 'my_editor' },
  placeholderText: 'Edit Your Content Here!',
  charCounterCount: true,
  reactIgnoreAttrs: ['class', 'id'],
  htmlAllowedAttrs: ['friendly_url', 'accept', 'accept-charset', 'accesskey', 'action', 'align', 'allowfullscreen', 'allowtransparency', 'alt', 'aria-.*', 'async', 'autocomplete', 'autofocus', 'autoplay', 'autosave', 'background', 'bgcolor', 'border', 'charset', 'cellpadding', 'cellspacing', 'checked', 'cite', 'class', 'color', 'cols', 'colspan', 'content', 'contenteditable', 'contextmenu', 'controls', 'coords', 'data', 'data-.*', 'datetime', 'default', 'defer', 'dir', 'dirname', 'disabled', 'download', 'draggable', 'dropzone', 'enctype', 'for', 'form', 'formaction', 'frameborder', 'headers', 'height', 'hidden', 'high', 'href', 'hreflang', 'http-equiv', 'icon', 'id', 'ismap', 'itemprop', 'keytype', 'kind', 'label', 'lang', 'language', 'list', 'loop', 'low', 'max', 'maxlength', 'media', 'method', 'min', 'mozallowfullscreen', 'multiple', 'muted', 'name', 'novalidate', 'open', 'optimum', 'pattern', 'ping', 'placeholder', 'playsinline', 'poster', 'preload', 'pubdate', 'radiogroup', 'readonly', 'rel', 'required', 'reversed', 'rows', 'rowspan', 'sandbox', 'scope', 'scoped', 'scrolling', 'seamless', 'selected', 'shape', 'size', 'sizes', 'span', 'src', 'srcdoc', 'srclang', 'srcset', 'start', 'step', 'summary', 'spellcheck', 'style', 'tabindex', 'target', 'title', 'type', 'translate', 'usemap', 'value', 'valign', 'webkitallowfullscreen', 'width', 'wrap'],
  imageAllowedTypes: ['jpeg', 'jpg', 'png', 'gif', 'webp', 'svg', 'svg+xml'],
  toolbarButtons: {
    moreText: {
      buttons: ['bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript', 'fontFamily', 'fontSize', 'textColor', 'backgroundColor', 'inlineClass', 'inlineStyle', 'clearFormatting'],
      buttonsVisible: 3
    },
    moreParagraph: {
      buttons: ['alignLeft', 'alignCenter', 'formatOLSimple', 'alignLeft', 'alignCenter', 'alignRight', 'alignJustify', 'formatOL', 'formatUL', 'paragraphFormat', 'paragraphStyle', 'lineHeight', 'outdent', 'indent', 'quote'],
      buttonsVisible: 2
    },
    moreRich: {
      buttons: ['insertLink', 'insertImage', 'insertVideo', 'insertTable', 'emoticons', 'fontAwesome', 'specialCharacters', 'embedly', 'insertFile', 'insertFiles', 'insertHR'],
      buttonsVisible: 3
    },
    moreMisc: {
      buttons: ['undo', 'redo', 'fullscreen', 'print', 'getPDF', 'spellChecker', 'selectAll', 'html', 'save', 'help'],
      align: 'right',
      buttonsVisible: 2
    }
  },
  fontFamilyDefaultSelection: 'futura-pt',
  fontFamilySelection: true,
  fontSizeSelection: true,
  fontFamily: {
    "futura-pt, Roboto, 'Open Sans Condensed', sans-serif": 'Futura, Roboto, Open Sans Condensed, sans-serif',
    'Arial,Helvetica,sans-serif': 'Arial,Helvetica,sans-serif',
    "Lato,'Helvetica Neue', Arial, Helvetica, sans-serif": "Lato, Helvetica Neue, Arial, Helvetica, sans-serif",
    "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif": "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif",
    "Verdana, Geneva, Tahoma, sans-serif": "Verdana, Geneva, Tahoma, sans-serif",
    "'Trebuchet MS', 'Lucida Sans Unicode', 'Lucida Grande', 'Lucida Sans', Arial, sans-serif": "'Trebuchet MS', 'Lucida Sans Unicode', 'Lucida Grande', 'Lucida Sans', Arial, sans-serif",
    "'Times New Roman', Times, serif": "'Times New Roman', Times, serif"
  },
  inlineClasses: {
    'bookTitle': 'Note Heading',
    'bookAuthor': 'Author Style',
    'bookDate': 'bookDate',
    'bookContent': 'Content Text',
  },
  paragraphStyles: {
    'voiceRecorder': 'Voice Recorder Container',
  },
  paragraphFormat: {
    N: 'Normal',
    H1: 'Heading 1',
    H2: 'Heading 2',
    H3: 'Heading 3',
    H4: 'Heading 4',
  },
  tableStyles: {
    'transparentTable': 'Transparent Style',
  },
  tableCellStyles: {
    'imagePadAll': 'Padding 10'
  },
  imageStyles: {
    'imageRounded': 'Rounded Image',
    'imagePadAll': 'Padding All 10'
  },
  fileUploadToS3: {
    bucket: 'editor',
    region: 's3',
    keyStart: 'uploads/',
    params: {
      acl: 'public-read',
      AWSAccessKeyId: 'ACCESS_KEY',
      policy: 'POLICY_STRING',
      signature: '',
    }
  }
};

export const characterTypeList = [
  { key: 'child', text: 'Child', value: 'child' },
  { key: 'teen', text: 'Teen', value: 'teen' },
  { key: 'young-adult', text: 'Young Adult', value: 'young-adult' },
  { key: 'adult', text: 'Adult', value: 'adult' },
  { key: 'senior', text: 'Senior', value: 'senior' },
  { key: 'dragon', text: 'Dragon', value: 'dragon' },
  { key: 'computer', text: 'Computer', value: 'computer' }
];

export const characterGenderList = [
  { key: 'male', text: 'Male', value: 'male' },
  { key: 'female', text: 'Female', value: 'female' },
  { key: 'non-binary', text: 'Non-Binary', value: 'non-binary' },
  { key: 'other', text: 'Other', value: 'other' }
];

// Consolidated action types for variation sample interactions and edit version management
export const ACTION_TYPES = {
  // Internal and User-facing actions (used by VariationVersionsList)
  // Internal edit version management actions (used by useEditVersionManager hook)
  SAMPLE_PREVIEW: 'sample_preview',
  SAMPLE_EDIT_SELECT: 'SAMPLE_EDIT_SELECT',
  SAMPLE_USE_AS_BASE: 'sample_use_as_base',
  SAMPLE_DELETE: 'sample_delete',
  ACTIVE_SAMPLE_LOAD: 'active_sample_load',
  FIELD_CHANGE: 'field_change',
  CANCEL_CHANGES: 'cancel_changes',
  CREATE_FROM_SAMPLE: 'create_from_sample',
  STATUS_CHANGE: 'status_change'
};
export const VERSION_TYPES = {
  ACTIVE_SAMPLE: 'active_sample',
  SPECIFIC_SAMPLE: 'specific_sample',
  BASE_VERSION: 'base_version',
  VARIATION_ONLY: 'variation_only'
};



export const DELETE_TYPES = {
  SOFT: 'soft',                    // Role-based soft delete (default)
  HARD: 'hard',                    // Permanent deletion
  ARCHIVE: 'archive'               // Archive (role = 'Archived')
};

// Entity Role States (NEW)
export const ENTITY_ROLES = {
  ACTIVE: 'Active',                // Normal active state
  DELETED: 'Deleted',              // Soft deleted state
  ARCHIVED: 'Archived',            // Archived state
  SUSPENDED: 'Suspended',          // Temporarily suspended
  UNDER_CONTRACT: 'UnderContract'  // Currently under contract (future)
};

// Export default config
export default config;
